import * as ES from './ecmascript';
import { MakeIntrinsicClass } from './intrinsicclass';
import { ISO_YEAR, ISO_MONTH, ISO_DAY, CALENDAR, GetSlot } from './slots';
import { DateTimeFormat } from './intl';
const ObjectCreate = Object.create;
export class PlainYearMonth {
    constructor(isoYearParam, isoMonthParam, calendarParam = ES.GetISO8601Calendar(), referenceISODayParam = 1) {
        const isoYear = ES.ToIntegerThrowOnInfinity(isoYearParam);
        const isoMonth = ES.ToIntegerThrowOnInfinity(isoMonthParam);
        const calendar = ES.ToTemporalCalendar(calendarParam);
        const referenceISODay = ES.ToIntegerThrowOnInfinity(referenceISODayParam);
        // Note: if the arguments are not passed,
        //       ToIntegerThrowOnInfinity(undefined) will have returned 0, which will
        //       be rejected by RejectISODate in CreateTemporalYearMonthSlots. This
        //       check exists only to improve the error message.
        if (arguments.length < 2) {
            throw new RangeError('missing argument: isoYear and isoMonth are required');
        }
        ES.CreateTemporalYearMonthSlots(this, isoYear, isoMonth, calendar, referenceISODay);
    }
    get year() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarYear(GetSlot(this, CALENDAR), this);
    }
    get month() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarMonth(GetSlot(this, CALENDAR), this);
    }
    get monthCode() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarMonthCode(GetSlot(this, CALENDAR), this);
    }
    get calendar() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return GetSlot(this, CALENDAR);
    }
    get era() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarEra(GetSlot(this, CALENDAR), this);
    }
    get eraYear() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarEraYear(GetSlot(this, CALENDAR), this);
    }
    get daysInMonth() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarDaysInMonth(GetSlot(this, CALENDAR), this);
    }
    get daysInYear() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarDaysInYear(GetSlot(this, CALENDAR), this);
    }
    get monthsInYear() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarMonthsInYear(GetSlot(this, CALENDAR), this);
    }
    get inLeapYear() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.CalendarInLeapYear(GetSlot(this, CALENDAR), this);
    }
    with(temporalYearMonthLike, optionsParam = undefined) {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        if (!ES.IsObject(temporalYearMonthLike)) {
            throw new TypeError('invalid argument');
        }
        ES.RejectObjectWithCalendarOrTimeZone(temporalYearMonthLike);
        const calendar = GetSlot(this, CALENDAR);
        const fieldNames = ES.CalendarFields(calendar, ['month', 'monthCode', 'year']);
        const props = ES.PrepareTemporalFields(temporalYearMonthLike, fieldNames, 'partial');
        if (!props) {
            throw new TypeError('invalid year-month-like');
        }
        let fields = ES.PrepareTemporalFields(this, fieldNames, []);
        fields = ES.CalendarMergeFields(calendar, fields, props);
        fields = ES.PrepareTemporalFields(fields, fieldNames, []);
        const options = ES.GetOptionsObject(optionsParam);
        return ES.CalendarYearMonthFromFields(calendar, fields, options);
    }
    add(temporalDurationLike, options = undefined) {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.AddDurationToOrSubtractDurationFromPlainYearMonth('add', this, temporalDurationLike, options);
    }
    subtract(temporalDurationLike, options = undefined) {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.AddDurationToOrSubtractDurationFromPlainYearMonth('subtract', this, temporalDurationLike, options);
    }
    until(other, options = undefined) {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.DifferenceTemporalPlainYearMonth('until', this, other, options);
    }
    since(other, options = undefined) {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.DifferenceTemporalPlainYearMonth('since', this, other, options);
    }
    equals(otherParam) {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        const other = ES.ToTemporalYearMonth(otherParam);
        for (const slot of [ISO_YEAR, ISO_MONTH, ISO_DAY]) {
            const val1 = GetSlot(this, slot);
            const val2 = GetSlot(other, slot);
            if (val1 !== val2)
                return false;
        }
        return ES.CalendarEquals(GetSlot(this, CALENDAR), GetSlot(other, CALENDAR));
    }
    toString(optionsParam = undefined) {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        const options = ES.GetOptionsObject(optionsParam);
        const showCalendar = ES.ToShowCalendarOption(options);
        return ES.TemporalYearMonthToString(this, showCalendar);
    }
    toJSON() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return ES.TemporalYearMonthToString(this);
    }
    toLocaleString(locales = undefined, options = undefined) {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return new DateTimeFormat(locales, options).format(this);
    }
    valueOf() {
        throw new TypeError('use compare() or equals() to compare Temporal.PlainYearMonth');
    }
    toPlainDate(item) {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        if (!ES.IsObject(item))
            throw new TypeError('argument should be an object');
        const calendar = GetSlot(this, CALENDAR);
        const receiverFieldNames = ES.CalendarFields(calendar, ['monthCode', 'year']);
        const fields = ES.PrepareTemporalFields(this, receiverFieldNames, []);
        const inputFieldNames = ES.CalendarFields(calendar, ['day']);
        const inputFields = ES.PrepareTemporalFields(item, inputFieldNames, []);
        let mergedFields = ES.CalendarMergeFields(calendar, fields, inputFields);
        // TODO: Use MergeLists abstract operation.
        const mergedFieldNames = [...new Set([...receiverFieldNames, ...inputFieldNames])];
        mergedFields = ES.PrepareTemporalFields(mergedFields, mergedFieldNames, []);
        const options = ObjectCreate(null);
        options.overflow = 'reject';
        return ES.CalendarDateFromFields(calendar, mergedFields, options);
    }
    getISOFields() {
        if (!ES.IsTemporalYearMonth(this))
            throw new TypeError('invalid receiver');
        return {
            calendar: GetSlot(this, CALENDAR),
            isoDay: GetSlot(this, ISO_DAY),
            isoMonth: GetSlot(this, ISO_MONTH),
            isoYear: GetSlot(this, ISO_YEAR)
        };
    }
    static from(item, optionsParam = undefined) {
        const options = ES.GetOptionsObject(optionsParam);
        if (ES.IsTemporalYearMonth(item)) {
            ES.ToTemporalOverflow(options); // validate and ignore
            return ES.CreateTemporalYearMonth(GetSlot(item, ISO_YEAR), GetSlot(item, ISO_MONTH), GetSlot(item, CALENDAR), GetSlot(item, ISO_DAY));
        }
        return ES.ToTemporalYearMonth(item, options);
    }
    static compare(oneParam, twoParam) {
        const one = ES.ToTemporalYearMonth(oneParam);
        const two = ES.ToTemporalYearMonth(twoParam);
        return ES.CompareISODate(GetSlot(one, ISO_YEAR), GetSlot(one, ISO_MONTH), GetSlot(one, ISO_DAY), GetSlot(two, ISO_YEAR), GetSlot(two, ISO_MONTH), GetSlot(two, ISO_DAY));
    }
}
Symbol.toStringTag;
MakeIntrinsicClass(PlainYearMonth, 'Temporal.PlainYearMonth');
//# sourceMappingURL=plainyearmonth.js.map